import { ReactNode } from 'react';

import { randomUUID as uuidv4 } from 'expo-crypto';
import { isUndefined, merge, omit } from 'lodash-es';

import {
  IBackendCartEntries,
  IBaseItem,
  ICombo,
  IComboSlot,
  IItem,
  IItemOption,
  IItemOptionModifierWithQuantity,
  ILegacyCartEntry,
  IModifierSelection,
  ISanityItemOptionModifier,
  ISanityVendorConfigs,
  IVendorConfig,
  IWithMenuObjectSettings,
} from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import {
  Brand,
  IPriceOrderInput,
  CartEntryType as LegacyCartEntryTypeGateway,
  PosVendor,
} from 'generated/graphql-gateway';
import { IPriceOrderParams, WISanityImage } from 'hooks/price-order/types';
import { LoyaltyOffer } from 'state/loyalty/types';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { clamp } from 'utils/calc';
import { platform } from 'utils/environment';
import { logger } from 'utils/logger';
import { getType } from 'utils/menu';
import { getDefaultComboSlotOption } from 'utils/menu/get-default-combo-slot-option';
import { makeItemOptionModifier } from 'utils/menu/modifiers';
import { priceForCartEntry } from 'utils/menu/price';
import { getFirstStringInLocaleBlockContent } from 'utils/sanity';

import { PluTypes, PosVendors } from '../vendor-config';

import { buildStoreAddress } from './build-store-address';
import {
  CartEntryType,
  ComboSlotUiPattern,
  ICartInput,
  IComboCartEntryOptions,
  IComboSlotCartEntryOptions,
  ICreateCartEntryOptions,
  IItemCartEntryOptions,
  IItemOptionCartEntryOptions,
  IItemOptionModifierCartEntryOptions,
  IOfferCartEntryOptions,
  IRestaurantWithPos,
} from './types';

// This number 9 doesn’t come from Sanity, this is cross brand based on POS limitations on double digit numbers.
// https://rbictg.atlassian.net/browse/CBA-1032
export const MAX_CART_QUANTITY = 9;
export const MIN_CART_QUANTITY = 1;

// If no sanity max amount is configured we use this value to prevent unexpected amount of quantity for same item.
export const MAX_ADD_ON_QUANTITY = 6;

const clampCartQuantity = clamp(MIN_CART_QUANTITY, MAX_CART_QUANTITY);

export const isOfferCombo = (cartEntry: ILegacyCartEntry) =>
  cartEntry.type === CartEntryType.offerCombo;

export const getMenuObjectLimitPerOrder = (
  menuObject: IWithMenuObjectSettings
): number | undefined => menuObject.menuObjectSettings?.limitPerOrder;

export const getMenuObjectQuantityInCart = (
  cartEntries?: ILegacyCartEntry[],
  menuObjectId?: string
) => {
  if (!cartEntries?.length || !menuObjectId) {
    return 0;
  }
  return cartEntries.reduce(
    (sum, cartEntry) => (cartEntry._id === menuObjectId ? sum + cartEntry.quantity : sum),
    0
  );
};

export const getMenuObjectMaxCartQuantity = (menuObject: IWithMenuObjectSettings): number => {
  const limitPerOrder = getMenuObjectLimitPerOrder(menuObject);
  if (typeof limitPerOrder === 'number' && limitPerOrder >= MIN_CART_QUANTITY) {
    return clampCartQuantity(Math.floor(limitPerOrder));
  }
  return MAX_CART_QUANTITY;
};

const DEFAULT_CART_QUANTITY_RETURN = {
  availableCartQuantity: MAX_CART_QUANTITY,
  maxCartQuantity: MAX_CART_QUANTITY,
  maxCartQuantityMet: false,
} as const;

export const getMenuObjectCartQuantity = ({
  menuObject,
  quantityToAdd = 1,
  cartEntries,
  cartIdEditing,
  unifiedMenuEntry,
}: {
  menuObject?: IWithMenuObjectSettings | null;
  quantityToAdd?: number;
  cartEntries?: ILegacyCartEntry[];
  cartIdEditing?: string;
  unifiedMenuEntry?: ILocalizedMenuEntry | null;
}): {
  availableCartQuantity: number;
  maxCartQuantity: number;
  maxCartQuantityMet: boolean;
} => {
  const isUsingUnifiedMenu = Boolean(unifiedMenuEntry?.quantity?.max);
  const menuObjectId = isUsingUnifiedMenu ? unifiedMenuEntry?.id : menuObject?._id;

  if (!menuObjectId) {
    return DEFAULT_CART_QUANTITY_RETURN;
  }

  const entries = cartEntries?.filter(entry => entry.cartId !== cartIdEditing);
  const quantityOnCart = getMenuObjectQuantityInCart(entries, menuObjectId);
  const maxCartQuantity = isUsingUnifiedMenu
    ? unifiedMenuEntry.quantity.max
    : getMenuObjectMaxCartQuantity(menuObject);

  const availableCartQuantity = maxCartQuantity - quantityOnCart;
  const maxCartQuantityMet = quantityToAdd > availableCartQuantity;

  return {
    availableCartQuantity,
    maxCartQuantity,
    maxCartQuantityMet,
  };
};

const StaticIgnoreConfig = Object.values(PosVendors).reduce(
  (acc, vendor) => ({
    ...acc,
    [vendor]: {
      pluType: PluTypes.IGNORE,
    },
  }),
  {}
);

export const sanitizeVendorConfigs = (
  vendorConfigs: ISanityVendorConfigs
): ISanityVendorConfigs => {
  const mappedVendorConfigs: ISanityVendorConfigs = {};
  Object.entries(vendorConfigs).forEach(([key, value]) => {
    const isVendorConfig = key !== 'type' && key !== '_type';
    const isEmpty = Boolean(value && !value?.pluType);
    if (isVendorConfig && isEmpty) {
      mappedVendorConfigs[key] = {
        ...(value as IVendorConfig),
        pluType: 'empty',
      };
    } else {
      mappedVendorConfigs[key] = value;
    }
  });
  return mappedVendorConfigs;
};

interface IBackendCartEntriesGateway extends Omit<IBackendCartEntries, 'children' | 'type'> {
  children: IBackendCartEntriesGateway[];
  type: LegacyCartEntryTypeGateway;
}

const cartEntryTypeToGatewayType = (type: CartEntryType): LegacyCartEntryTypeGateway => {
  switch (type) {
    case CartEntryType.combo:
      return LegacyCartEntryTypeGateway.COMBO;
    case CartEntryType.comboSlot:
      return LegacyCartEntryTypeGateway.COMBOSLOT;
    case CartEntryType.itemOption:
      return LegacyCartEntryTypeGateway.ITEMOPTION;
    case CartEntryType.itemOptionModifier:
      return LegacyCartEntryTypeGateway.ITEMOPTIONMODIFIER;
    case CartEntryType.offerCombo:
      return LegacyCartEntryTypeGateway.OFFERCOMBO;
    case CartEntryType.offerDiscount:
      return LegacyCartEntryTypeGateway.OFFERDISCOUNT;
    case CartEntryType.offerItem:
      return LegacyCartEntryTypeGateway.OFFERITEM;
    case CartEntryType.item:
    default:
      return LegacyCartEntryTypeGateway.ITEM;
  }
};

export const frontendToBackendCartEntryMap = ({
  cartId,
  children,
  pickerSelections,
  image,
  sanityId,
  redeemable, // eslint-disable-line @typescript-eslint/no-unused-vars
  _id, // eslint-disable-line @typescript-eslint/no-unused-vars
  uiPattern, // eslint-disable-line @typescript-eslint/no-unused-vars
  rewardEligible, // eslint-disable-line @typescript-eslint/no-unused-vars
  isUpsell, // eslint-disable-line @typescript-eslint/no-unused-vars
  reorder, // eslint-disable-line @typescript-eslint/no-unused-vars
  menuObjectSettings, // eslint-disable-line @typescript-eslint/no-unused-vars
  mainItem, // eslint-disable-line @typescript-eslint/no-unused-vars
  defaultComboSlotOption, // eslint-disable-line @typescript-eslint/no-unused-vars
  enableUpsellPricing, // eslint-disable-line @typescript-eslint/no-unused-vars
  nutritionWithModifiers, // eslint-disable-line @typescript-eslint/no-unused-vars
  modifierMultiplier, // eslint-disable-line @typescript-eslint/no-unused-vars
  vendorConfigs,
  offerVendorConfigs,
  type,
  ...rest
}: ILegacyCartEntry & { redeemable?: unknown }): IBackendCartEntriesGateway => {
  const remapped: IBackendCartEntriesGateway = {
    lineId: cartId,
    // Offer Cart Entries have a sanityId
    // Other entries do not
    sanityId: sanityId || _id,
    children: children.map(frontendToBackendCartEntryMap),
    image: JSON.stringify(image),
    vendorConfigs: vendorConfigs ? sanitizeVendorConfigs(vendorConfigs) : vendorConfigs,
    offerVendorConfigs: offerVendorConfigs
      ? sanitizeVendorConfigs(offerVendorConfigs)
      : offerVendorConfigs,
    type: cartEntryTypeToGatewayType(type),
    ...rest,
  };

  if (pickerSelections) {
    remapped.pickerSelections = JSON.stringify(pickerSelections);
  }

  return remapped;
};

export const remappedCartForBackEnd = (cart: ILegacyCartEntry[]): IBackendCartEntriesGateway[] => {
  return cart.map(frontendToBackendCartEntryMap);
};

const getCartEntryForCombo = (
  combo: ICombo,
  options?: IComboCartEntryOptions
): ILegacyCartEntry => {
  const {
    cartId: existingCartId,
    comboSlotSelections = {},
    modifierSelections = [],
    pickerSelections = {},
    price,
    quantity,
    pathname: url = '',
    reorder,
    menuObjectSettings,
  } = options ?? {};
  const cartId = existingCartId || uuidv4();
  let children: ILegacyCartEntry[] = [];
  const comboSlots = Object.values(comboSlotSelections);

  if (combo.mainItem) {
    // modifiers with no combo slot id correspond to the main item
    const mainItemModifiers = modifierSelections.filter(({ comboSlotId }) =>
      isUndefined(comboSlotId)
    );

    children.push(
      createCartEntry({
        item: combo.mainItem,
        modifierSelections: mainItemModifiers,
      })
    );
  }

  children = [
    ...children,
    ...comboSlots.map(slot =>
      createCartEntry({
        // use the raw data so we have access to all the options, fallback to the selection data just in case(should never hit this case)...
        item: combo.options.find(comboSlot => comboSlot._id === slot.data._id) ?? slot.data,
        selections: slot.selections,
        modifierSelections: modifierSelections.filter(
          ({ comboSlotId }) => comboSlotId === slot.data._id
        ),
      })
    ),
  ];

  return {
    _id: combo._id,
    type: CartEntryType.combo,
    quantity: quantity || 1,
    sanityId: '',
    url,
    name: combo.name.locale,
    vendorConfigs: combo.vendorConfigs,
    image: combo.image,
    cartId,
    lineId: cartId,
    children,
    pickerSelections,
    menuObjectSettings: combo.menuObjectSettings || menuObjectSettings,
    mainItem: combo.mainItem,
    ...(reorder && { reorder }),
    ...(price !== undefined && !isNaN(price) && { price }),
  };
};

const getCartEntryForComboSlot = (
  comboSlot: IComboSlot,
  options?: IComboSlotCartEntryOptions
): ILegacyCartEntry => {
  const { cartId: existingCartId, selections, pathname: url = '', modifierSelections = [] } =
    options ?? {};
  const cartId = existingCartId || uuidv4();
  let children: ILegacyCartEntry[] = []; // Is this being used at all???

  children = [
    ...children, // Is this being used at all???
    ...(selections || []).map(selection =>
      createCartEntry({
        item: selection.option.option,
        quantity: selection.quantity,
        modifierSelections,
        price: 0, // Priced later on repriceCartEntries
        enableUpsellPricing: selection.option.enableUpsellPricing,
      })
    ),
  ];

  const defaultComboSlotOption = getDefaultComboSlotOption(comboSlot);

  return {
    _id: comboSlot._id,
    type: CartEntryType.comboSlot,
    cartId,
    lineId: cartId,
    quantity: 1,
    sanityId: '',
    url,
    name: comboSlot.name.locale,
    vendorConfigs: comboSlot.vendorConfigs,
    uiPattern: comboSlot.uiPattern as ComboSlotUiPattern,
    pickerSelections: {},
    children,
    defaultComboSlotOption,
  };
};

const getCartEntryForItem = (
  item: IItem | IBaseItem,
  options?: IItemCartEntryOptions
): ILegacyCartEntry => {
  const {
    price,
    quantity,
    cartId: existingCartId,
    pathname: url = '',
    modifierSelections = [],
    pickerSelections = {},
    reorder,
    menuObjectSettings,
    enableUpsellPricing,
  } = options ?? {};
  const cartId = existingCartId || uuidv4();
  let children: ILegacyCartEntry[] = [];

  if (modifierSelections) {
    const itemOpts = modifierSelections.filter(selection => selection.itemId === item._id);
    children = composeItemOptionModifiers(itemOpts).map(selection =>
      // relate the item to the item option modifiers
      createCartEntry({
        item: selection,
      })
    );
    // check if there are modifier required for given item
    // and if there is any default modifier within the list
    if ((item as IItem)?.options?.length > 0) {
      (item as IItem).options.forEach(itemOption => {
        const numberOfModifierAlreadySelected = itemOpts.reduce(
          (prevQuantity, selectedItemOption) => {
            if (selectedItemOption._key === itemOption._key) {
              prevQuantity += selectedItemOption.modifier.quantity;
            }
            return prevQuantity;
          },
          0
        );
        const defaultItemOption = itemOption.options.find(modifierOption => modifierOption.default);
        // we will take the first default modifier and add enough to meet minAmount
        if (
          itemOption.injectDefaultSelection &&
          defaultItemOption &&
          numberOfModifierAlreadySelected < itemOption.minAmount
        ) {
          const defaultItemOptionModifierCartEntry = getCartEntryForItemOptionModifier(
            {
              ...defaultItemOption,
              quantity: itemOption.minAmount - numberOfModifierAlreadySelected,
            },
            {}
          );
          const itemOptionCartEntry = getCartEntryForItemOption(itemOption, {});
          // will override the item option modifier with the default modifier
          itemOptionCartEntry.children = [defaultItemOptionModifierCartEntry];
          children.push(itemOptionCartEntry);
        }
      });
    }
  }

  return {
    _id: item._id,
    type: CartEntryType.item,
    cartId,
    lineId: cartId,
    sanityId: '',
    url,
    name: item.name.locale,
    vendorConfigs: item.vendorConfigs,
    image: item.image,
    isDummyItem: item.isDummyItem,
    quantity: quantity || 1,
    children,
    pickerSelections,
    productHierarchy: item.productHierarchy,
    menuObjectSettings: item.menuObjectSettings || menuObjectSettings,
    enableUpsellPricing,
    ...(reorder && { reorder }),
    ...(price !== undefined && !isNaN(price) && { price }),
    nutritionWithModifiers: item.nutritionWithModifiers,
    productNumber: item.operationalItem?.productNumber,
  };
};

const getCartEntryForItemOption = (
  itemOption: IItemOption,
  options?: IItemOptionCartEntryOptions
): ILegacyCartEntry => {
  let children: ILegacyCartEntry[] = [];
  const { cartId: existingCartId, pathname: url = '' } = options ?? {};
  const cartId = existingCartId || uuidv4();

  children = itemOption.options.map(modifier =>
    // TODO: Review this logic. modifier.modifierMultiplier?.multiplier can be 0
    createCartEntry({
      item: !modifier.modifierMultiplier?.multiplier
        ? makeItemOptionModifier(modifier as ISanityItemOptionModifier)
        : modifier, // guarantee our itemOption modifier is always the correct shape
    })
  );

  return {
    _id: itemOption._key,
    name: itemOption.name.locale,
    cartId,
    lineId: cartId,
    children,
    quantity: 1,
    vendorConfigs: StaticIgnoreConfig,
    pickerSelections: {},
    sanityId: '',
    type: CartEntryType.itemOption,
    url,
  };
};

const getCartEntryForItemOptionModifier = (
  itemOptionModifier: IItemOptionModifierWithQuantity,
  options?: IItemOptionModifierCartEntryOptions
): ILegacyCartEntry => {
  const { cartId: existingCartId, pathname: url = '' } = options ?? {};
  const cartId = existingCartId || uuidv4();

  return {
    _id: itemOptionModifier._key,
    cartId,
    lineId: cartId,
    sanityId: '',
    children: [],
    type: CartEntryType.itemOptionModifier,
    vendorConfigs: itemOptionModifier.vendorConfigs
      ? sanitizeVendorConfigs(itemOptionModifier.vendorConfigs)
      : {},
    name: itemOptionModifier.name?.locale ?? '',
    modifierMultiplier: itemOptionModifier.modifierMultiplier,
    url,
    price: 0, // Priced later on repriceCartEntries
    quantity:
      (itemOptionModifier.quantity || itemOptionModifier.modifierMultiplier?.multiplier) ?? 1,
    pickerSelections: {},
  };
};

const getCartEntryForOfferDiscount = (
  offer: LoyaltyOffer,
  options?: IOfferCartEntryOptions
): ILegacyCartEntry => {
  const { cartId: existingCartId, pathname: url = '' } = options ?? {};
  const cartId = existingCartId || uuidv4();

  return {
    _id: offer._id,
    cartId,
    lineId: cartId,
    sanityId: '',
    children: [],
    type: CartEntryType.offerDiscount,
    // TODO: Review SystemwideOffers vendorConfigs worth. Maybe we could remove it from the types too
    vendorConfigs: {},
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ readonly __typename?: "LocaleB... Remove this comment to see the full error message
    name: getFirstStringInLocaleBlockContent(offer.name),
    url,
    quantity: 1,
    pickerSelections: {},
  };
};

export const createCartEntry = (options: ICreateCartEntryOptions): ILegacyCartEntry => {
  const { item } = options;
  const itemType = getType(item);
  if (!itemType) {
    logger.error({ message: '[createCartEntry] _type or type is missing on item', item, options });
    throw new Error('_type or type is missing on item');
  }

  // TODO: Handle Pickers or remove them as ComboSlot options. ComboSlot options can be pickers, we currently are not handling pickers...
  switch (itemType) {
    case MenuObjectTypes.COMBO:
      return getCartEntryForCombo(item as ICombo, options as IComboCartEntryOptions);
    case MenuObjectTypes.ITEM:
      return getCartEntryForItem(item as IItem | IBaseItem, options as IItemCartEntryOptions);
    case MenuObjectTypes.COMBO_SLOT:
      return getCartEntryForComboSlot(item as IComboSlot, options as IComboSlotCartEntryOptions);
    case MenuObjectTypes.ITEM_OPTION:
      return getCartEntryForItemOption(item as IItemOption, options as IItemOptionCartEntryOptions);
    case MenuObjectTypes.ITEM_OPTION_MODIFIER:
      return getCartEntryForItemOptionModifier(
        item as IItemOptionModifierWithQuantity,
        options as IItemOptionModifierCartEntryOptions
      );
    case 'offer':
    case 'systemwideOffer':
    case 'configOffer':
      return getCartEntryForOfferDiscount(item as LoyaltyOffer, options as IOfferCartEntryOptions);
    default:
      logger.error({
        message: '[createCartEntry] trying to derive a type from a non-defined type',
        item,
        options,
      });
      throw new Error('trying to derive a type from a non-defined type');
  }
};

const composeItemOptionModifiers = (
  modifierSelections: IModifierSelection[]
): IItemOptionModifierWithQuantity[] => {
  // TODO: At some point we may have itemOptions that are duplicated for a cartEnrty, and will need to add more uniqueness to the keys
  const reducedItemOpts = modifierSelections.reduce((acc, option) => {
    if (acc[option._key]) {
      acc[option._key].options.push(makeItemOptionModifier(option.modifier));
      return acc;
    }
    const { _key, modifier, ...rest } = option;
    return {
      ...acc,
      [_key]: {
        _key,
        ...rest,
        options: [makeItemOptionModifier(modifier)],
      },
    };
  }, {});

  return Object.values(reducedItemOpts);
};

/**
 * This method will take the url from a cart entry and return the id the entry originated from
 * in the case of a resolved picker option, the id will be the picker's
 * in the case when multiple items are added from a single combo, the id will be the combo's
 *
 *
 * *NOTE* urls can come in two flavors
 * * example url 1 - /menu/picker/$pickerId?cartId=$cartId&_id=$itemId
 * * example url 2 - /menu/picker/$pickerId
 * @param {String} url
 * @returns {String} parentId
 */
export const getParentIdFromUrl = (url: string = ''): string => {
  if (!url) {
    return url;
  }

  const [pathWithParentId] = url.split('?');
  const parentIdIdx = pathWithParentId.lastIndexOf('/') + 1;

  const rawParentId = pathWithParentId.slice(parentIdIdx);
  return rawParentId;
};

/**
 * Gets a list of selections modifiers/comboSlot items to display to the guest for an entry
 * Will not display the parents only the lowest level selections (combo slots and item options are skipped)
 *
 * Note: This is different from the cart
 * (https://github.com/rbilabs/ctg-whitelabel-app/blob/GBR-510-favorite-cards/workspaces/frontend/src/hooks/use-compose-description.ts)
 *  The cart will group selections by combo slot item and main item displaying them. While this one will skip those completely
 *  without showing them and just list the modifiers/items within them.
 *
 *  Also this cart hook will make and extra menu request which is not needed if you do that before rendering.
 */
export const getSelectionsListFromEntry = (
  entry: ILegacyCartEntry | null,
  child?: boolean
): { id: string; isDummyItem?: boolean; element: ReactNode; type: CartEntryType }[] => {
  const removeDuplicates = (arr: ILegacyCartEntry[]) =>
    arr.filter((value, index) => {
      return (
        index ===
        entry?.children?.findIndex(obj => {
          return obj.name === value.name;
        })
      );
    });

  // Exit early if there is no entry or the entry has no selections
  if (!entry || (!child && !entry.children?.length)) {
    return [];
  }

  // some comboSlots should be hidden from the guest
  if (
    (entry.type === CartEntryType.comboSlot && entry.uiPattern === ComboSlotUiPattern.HIDDEN) ||
    entry.uiPattern === ComboSlotUiPattern.COLLAPSED
  ) {
    return [];
  }

  const results: {
    id: string;
    isDummyItem?: boolean;
    element: ReactNode;
    type: CartEntryType;
  }[] = [];

  if (entry.type === CartEntryType.item || entry.type === CartEntryType.itemOptionModifier) {
    results.push({
      id: entry.lineId || entry.cartId || entry._id,
      isDummyItem: entry.isDummyItem,
      element: entry.name ?? '',
      type: entry.type,
    });
  }

  if (entry.children?.length) {
    const childResults = removeDuplicates(entry.children).flatMap(childEntry =>
      getSelectionsListFromEntry(childEntry, true)
    );
    results.push(...childResults);
  }

  return results;
};

export function buildCartInput({ cartEntries, serviceMode, store }: ICartInput): IPriceOrderInput {
  return {
    storeAddress: buildStoreAddress(store),
    storeId: String(store.number),
    storePosId: store.posRestaurantId,
    brand: Brand.BK,
    platform: platform(),
    posVendor: store.pos.vendor as PosVendor,
    serviceMode,
    requestedAmountCents: cartEntries.reduce((price, entry) => price + priceForCartEntry(entry), 0),
    cartEntries: remappedCartForBackEnd(cartEntries),
    vatNumber: String(store.vatNumber),
  };
}

export const sanitizeSanityImage = (image: WISanityImage) => {
  // We're knowingly breaking the contract of ILegacyCartEntry and ISanityImage here
  // we can remove these once we update @rbi-ctg/menu ILegacyCartEntry and ISanityImage
  // to make these properties options
  delete image?.asset?.metadata;
};

export const sanitizeCart = (
  cartEntries: ILegacyCartEntry[],
  vendor: PosVendors | null
): ILegacyCartEntry[] => {
  return cartEntries.map(cartEntry => {
    const {
      defaultComboSlotOption,
      mainItem,
      enableUpsellPricing,
      offerVendorConfigs,
      ...cartEntryWithOmittedProps
    } = cartEntry;
    if (cartEntryWithOmittedProps.image) {
      sanitizeSanityImage(cartEntryWithOmittedProps.image as WISanityImage);
    }

    return {
      ...cartEntryWithOmittedProps,
      vendorConfigs: vendor
        ? { [vendor]: cartEntry.vendorConfigs?.[vendor] }
        : cartEntry.vendorConfigs,
      offerVendorConfigs:
        offerVendorConfigs && vendor
          ? { [vendor]: cartEntry.offerVendorConfigs?.[vendor] }
          : cartEntry.offerVendorConfigs,
      children: sanitizeCart(cartEntry.children, vendor),
    };
  });
};

const hasPosVendor = (
  options: Omit<IPriceOrderParams, 'cartEntries' | 'redeemReward'>
): options is Omit<IPriceOrderParams, 'cartEntries' | 'redeemReward' | 'store'> & {
  store: IRestaurantWithPos;
} => {
  return !!options.store.pos?.vendor;
};

export function buildPriceOrderInput({
  cartEntries,
  options,
  vendor,
}: {
  cartEntries: ILegacyCartEntry[];
  options: Omit<IPriceOrderParams, 'cartEntries' | 'redeemReward'>;
  vendor: PosVendors | null;
}): IPriceOrderInput | undefined {
  // Can't price an order if:
  //  - store is unavailable
  //  - store doesn't have a POS vendor associated
  if (!options.isStoreOpenAndAvailable || !vendor || !hasPosVendor(options)) {
    return;
  }

  const params = merge(
    {
      cartEntries: sanitizeCart(cartEntries, vendor),
    },
    options
  );

  const lineIdByCartId = params.cartEntries.reduce(
    (acm, cartEnrty) => ({
      ...acm,
      [cartEnrty.cartId]: cartEnrty.lineId,
    }),
    {}
  );

  const priceOrderInput = {
    ...buildCartInput(params),
    customerLocale: params.customerLocale,
    customerName: params.customerName,
    paymentMethod: params.cardType,
    rewardsApplied: params.rewardsApplied?.map(appliedReward => ({
      // Remove field used only for the FE, sending it makes the mutation fail
      ...omit(appliedReward, ['redemptionMethod']),
      // BE expects in `cartId` the `lineId` of the corresponding cart entry.
      // If the lineId is not found, use the cartId.
      cartId: lineIdByCartId[appliedReward.cartId] || appliedReward.cartId,
    })),
    appliedOffers: params.appliedOffers?.map(appliedOffer => ({
      ...appliedOffer,
      // BE expects in `cartId` the `lineId` of the corresponding cart entry.
      // If the lineId is not found, use the cartId.
      cartId: lineIdByCartId[appliedOffer.cartId] || appliedOffer.cartId,
    })),
    requestedAmountCents: params.requestedAmountCents,
    redeemReward: false,
    vatNumber: params.store.vatNumber,
  };

  if (priceOrderInput.requestedAmountCents === 0) {
    //  If we have only one entry and one reward applied, we don't need to log
    // a warning because this is a valid scenario
    if (
      cartEntries.length === 1 &&
      priceOrderInput.rewardsApplied &&
      priceOrderInput.rewardsApplied.length === 1
    ) {
      return priceOrderInput;
    }
    logger.warn({
      message: '[buildPriceOrderInput] requestedAmountCents is 0',
      context: {
        cartEntries,
        options,
        priceOrderInput,
      },
    });
  }

  return priceOrderInput;
}

export { buildPriceDeliveryInput } from './build-price-delivery-input';
export { buildStoreAddress } from './build-store-address';

export const isDummyCartEntry = (cartEntry: ILegacyCartEntry | IBackendCartEntries) => {
  const hasLoyaltyMarker = 'lineId' in cartEntry && cartEntry.lineId === 'loyalty';
  const hasDummyMarker = ['combo sides', 'combo item'].includes(
    cartEntry.name?.toLocaleLowerCase().trim() ?? ''
  );
  return (hasDummyMarker || hasLoyaltyMarker) && cartEntry.children?.length === 0;
};

export const createCartEntriesFromQuantity = (cartEntry: ILegacyCartEntry) => {
  const cartEntries = [];
  for (let i = 0; i < cartEntry.quantity; i++) {
    const newId = uuidv4();

    const entry = {
      ...cartEntry,
      quantity: 1,
      cartId: newId,
      lineId: newId,
    };

    cartEntries.push(entry);
  }

  return cartEntries;
};

/**
 * Recursively sanitizes cart entries in preparation for reprice.
 * The puporse of this function is to make legacy and one menu cart entries compatible for repricing.
 *
 * @param {ILegacyCartEntry[]} cartEntries - The list of cart entries to sanitize.
 * @param {boolean} [isRoot=true] - Whether the current call is at the root level.
 * @param {CartEntryType} [parentType] - The type of the parent entry, used to determine pricing rules for children.
 * @returns {ILegacyCartEntry[]} A new array of sanitized cart entries ready for repricing.
 */
export const sanitizeCartForReprice = (
  cartEntries: ILegacyCartEntry[],
  isRoot = true,
  parentType?: CartEntryType
): ILegacyCartEntry[] => {
  const entriesToUpdate = [...cartEntries];

  return entriesToUpdate.map(entry => {
    let newPrice: number | undefined;

    const { children, price, type, ...rest } = entry;

    const isOffer = isOfferCombo(entry);

    if (isRoot && !isOffer) {
      newPrice = 0;
    } else if (isOffer) {
      newPrice = price;
    } else if (type === CartEntryType.item) {
      if (parentType === CartEntryType.comboSlot) {
        newPrice = 0;
      } else {
        newPrice = undefined;
      }
    } else {
      newPrice = 0;
    }

    return {
      ...rest,
      type,
      price: newPrice,
      children: sanitizeCartForReprice(children ?? [], false, type),
    };
  });
};
